import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Service, PagedResponse } from "../types";
import { serviceService } from "../services";

interface ServicesState {
  services: Service[];
  selectedService: Service | null;
  totalElements: number;
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: ServicesState = {
  services: [],
  selectedService: null,
  totalElements: 0,
  totalPages: 0,
  currentPage: 0,
  isLoading: false,
  error: null,
};

export const fetchServices = createAsyncThunk<
  PagedResponse<Service>,
  { page?: number; size?: number },
  { rejectValue: string }
>("services/fetchAll", async ({ page = 0, size = 20 }, { rejectWithValue }) => {
  try {
    const response = await serviceService.getAll(page, size);
    return response;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch services",
    );
  }
});

export const fetchServiceById = createAsyncThunk<
  Service,
  number,
  { rejectValue: string }
>("services/fetchById", async (id, { rejectWithValue }) => {
  try {
    const response = await serviceService.getById(id);
    return response;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch service",
    );
  }
});

const servicesSlice = createSlice({
  name: "services",
  initialState,
  reducers: {
    setSelectedService: (state, action: PayloadAction<Service | null>) => {
      state.selectedService = action.payload;
    },
    clearServicesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all services
      .addCase(fetchServices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.services = action.payload.content;
        state.totalElements = action.payload.totalElements;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.number;
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch services";
      })
      // Fetch service by ID
      .addCase(fetchServiceById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchServiceById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedService = action.payload;
      })
      .addCase(fetchServiceById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch service";
      });
  },
});

export const { setSelectedService, clearServicesError } = servicesSlice.actions;
export default servicesSlice.reducer;
